import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { users } from "../drizzle/users.js";
import { UserGroups } from "./auth.js";
export const userSchema = createSelectSchema(users, {
    id: (schema) => schema.meta({
        description: "User ID (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    // Use z.coerce.date() for date fields because JSON serialization converts Date objects to ISO strings
    createdAt: () => z.coerce.date().meta({
        description: "User creation timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    updatedAt: () => z.coerce.date().meta({
        description: "User last update timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    firstName: (schema) => schema.meta({
        description: "User first name",
        example: "John",
    }),
    lastName: (schema) => schema.meta({
        description: "User last name",
        example: "Doe",
    }),
    email: (schema) => schema.meta({
        description: "User email address",
        example: "john.doe@example.com",
    }),
    personioUserId: (schema) => schema.meta({
        description: "Personio user ID",
        example: "550e8400-e29b-41d4-a716-446655440000",
    }),
    callOneUserId: (schema) => schema.meta({
        description: "CallOne user ID",
        example: "550e8400-e29b-41d4-a716-446655440001",
    }),
    skillLevel: (schema) => schema.meta({
        description: "User skill level",
        example: "intermediate",
    }),
    roles: (schema) => schema.meta({
        description: "Keycloak roles (realm + client roles)",
        example: ["create-buckets", "update-buckets"],
    }),
    groups: (schema) => schema.meta({
        description: "Keycloak groups (organizational structure)",
        example: ["Developers", "Team-A"],
    }),
}).describe("User object");
// Base schema using callbacks - stays in sync with Drizzle column types
const baseCreateUserSchema = createInsertSchema(users, {
    id: () => z.uuid({ message: "Bitte gib eine gültige Keycloak-ID an" }).meta({
        description: "User ID (Keycloak UUID)",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    firstName: (schema) => schema.min(1, "Bitte gib einen Vornamen an").meta({
        description: "User first name",
        example: "John",
    }),
    lastName: (schema) => schema.min(1, "Bitte gib einen Nachnamen an").meta({
        description: "User last name",
        example: "Doe",
    }),
    email: () => z.email({ message: "Bitte gib eine gültige E-Mail-Adresse an" }).meta({
        description: "User email address",
        example: "john.doe@example.com",
    }),
    personioUserId: (schema) => schema
        .meta({
        description: "Personio user ID",
        example: "550e8400-e29b-41d4-a716-446655440000",
    })
        .nullable(),
    callOneUserId: (schema) => schema
        .meta({
        description: "CallOne user ID",
        example: "550e8400-e29b-41d4-a716-446655440001",
    })
        .nullable(),
    skillLevel: (schema) => schema.min(1, "Bitte gib ein Skill Level an").meta({
        description: "User skill level",
        example: "intermediate",
    }),
});
// Fix drizzle-zod bug: callbacks for nullable fields lose .optional() wrapper
// We extend the base schema to add .optional() to nullable fields
export const createUserSchema = baseCreateUserSchema
    .extend({
    personioUserId: baseCreateUserSchema.shape.personioUserId.optional(),
    callOneUserId: baseCreateUserSchema.shape.callOneUserId.optional(),
})
    .describe("Create user request body");
// Schema for syncing a single user from Keycloak
export const keycloakUserSyncSchema = z
    .object({
    id: z.uuid({ message: "Bitte gib eine gültige Keycloak-ID an" }).meta({
        description: "Keycloak user UUID",
        example: "afe710c0-595d-4e4e-ab70-7c5900a14457",
    }),
    firstName: z.string().min(1, "Vorname ist ein Pflichtfeld").meta({
        description: "User first name from Keycloak",
        example: "John",
    }),
    lastName: z.string().min(1, "Nachname ist ein Pflichtfeld").meta({
        description: "User last name from Keycloak",
        example: "Doe",
    }),
    email: z.email({ message: "Bitte gib eine gültige E-Mail-Adresse an" }).meta({
        description: "User email from Keycloak",
        example: "john.doe@example.com",
    }),
    // Roles and groups are optional for single-user sync — if not provided, they won't be updated.
    // For batch sync, see keycloakUserSyncBatchItemSchema which makes these required.
    roles: z
        .array(z.string())
        .optional()
        .meta({
        description: "Keycloak roles (realm + client roles)",
        example: ["create-buckets", "update-buckets"],
    }),
    groups: z
        .array(z.string())
        .optional()
        .meta({
        description: "Keycloak groups (organizational structure)",
        example: ["Developers", "Team-A"],
    }),
})
    .describe("Keycloak user sync payload");
// Batch sync requires roles and groups (unlike single-user sync where they're optional).
// The batch DB layer always overwrites roles/groups, so omitting them would silently wipe existing values.
const keycloakUserSyncBatchItemSchema = keycloakUserSyncSchema.extend({
    roles: z.array(z.string()).meta({
        description: "Keycloak roles (realm + client roles)",
        example: ["create-buckets", "update-buckets"],
    }),
    groups: z.array(z.string()).meta({
        description: "Keycloak groups (organizational structure)",
        example: ["Developers", "Team-A"],
    }),
});
export const keycloakUserSyncBatchSchema = z
    .object({
    users: z.array(keycloakUserSyncBatchItemSchema).min(1, "At least one user is required").meta({
        description: "Array of users to sync from Keycloak",
    }),
})
    .describe("Batch user sync request body");
// Response schema for sync operations
export const syncResponseSchema = z
    .object({
    synced: z.number().meta({
        description: "Number of users synced",
        example: 1,
    }),
    users: z.array(userSchema).meta({
        description: "Synced user objects",
    }),
})
    .describe("User sync response");
/**
 * Allowed ePort team groups for filtering users via the GET /users endpoint.
 *
 * Although Keycloak is the single source of truth for users, groups, and roles
 * (synced to the DB automatically), we do not use the raw Keycloak groups
 * property directly for filtering. Instead, we explicitly define which team
 * groups are valid filter values. This prevents unintended results — e.g. if a
 * user belongs to "EPORT_TEAM_LEAD", filtering by that group would also return
 * all team leads rather than members of a specific team.
 *
 * If a new team is added in Keycloak and should be available as a filter,
 * this object must be updated accordingly.
 *
 * The frontend also uses these values to restrict its filter UI to only the
 * groups the backend supports.
 */
export const UserGroupFilterValues = {
    FINANCE: UserGroups.FINANCE,
    REPAIR: UserGroups.REPAIR,
    DAMAGE: UserGroups.DAMAGE,
    CUSTOMER_RELATIONS: UserGroups.CUSTOMER_RELATIONS,
};
/** Non-empty tuple of all allowed group filter values (required by z.enum). */
const userGroupFilterValuesArray = Object.values(UserGroupFilterValues);
/** Zod enum schema for validating a single UserGroupFilterValue string. */
export const userGroupFilterValueSchema = z.enum(userGroupFilterValuesArray);
// Query schema for GET /users endpoint.
// Accepts any string(s) — unknown groups are silently ignored at the service layer.
// Handles both ?groups=A (string) and ?groups=A&groups=B (array) from query strings.
export const getUsersQuerySchema = z
    .object({
    groups: z
        .union([z.string(), z.array(z.string())])
        .transform((val) => (Array.isArray(val) ? val : [val]))
        .optional()
        .meta({
        description: "Filter users by ePort team group(s). Only the following groups are supported: EPORT_TEAM_FINANCE, EPORT_TEAM_REPAIR, EPORT_TEAM_DAMAGE, EPORT_TEAM_CUSTOMER_RELATIONS. Unknown groups are silently ignored.",
        example: "EPORT_TEAM_REPAIR",
    }),
})
    .describe("Get users query parameters");
